import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Search, MapPin, ChevronRight, User, CheckCircle2,
  Star, Clock, Filter, ArrowLeft, Briefcase,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVAIL_STYLES = {
  available:   { label: 'Müsait',       cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  busy:        { label: 'Meşgul',       cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  unavailable: { label: 'Müsait Değil', cls: 'bg-gray-100 text-gray-500 border-gray-300' },
};

function FreelancerCard({ f }) {
  const avStyle = AVAIL_STYLES[f.availability || 'available'];
  return (
    <Link to={`/freelancer/${f.slug}`}
      className="group bg-white rounded-2xl border border-gray-100 hover:border-red-200 shadow-sm hover:shadow-md transition-all p-5 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        {f.avatar_url ? (
          <img src={f.avatar_url} alt={f.full_name} className="w-12 h-12 rounded-xl object-cover border border-gray-100 shrink-0" />
        ) : (
          <div className="w-12 h-12 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
            <User className="w-6 h-6 text-red-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-gray-900 text-sm group-hover:text-red-700 transition-colors truncate">{f.full_name}</p>
            {f.is_verified && (
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
            )}
          </div>
          <p className="text-red-500 text-xs font-medium mt-0.5">{f.category_name}</p>
        </div>
        <Badge className={`text-[10px] border shrink-0 ${avStyle.cls}`}>{avStyle.label}</Badge>
      </div>

      {f.headline && (
        <p className="text-gray-500 text-xs line-clamp-2 leading-relaxed">{f.headline}</p>
      )}

      {(f.skills || []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {f.skills.slice(0, 4).map(s => (
            <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-50 text-gray-500 border border-gray-100">{s}</span>
          ))}
          {f.skills.length > 4 && <span className="text-[10px] text-gray-400">+{f.skills.length - 4}</span>}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-gray-400 border-t border-gray-50 pt-3">
        <span className="flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {f.city || f.country || 'Konum belirtilmemiş'}
        </span>
        <div className="flex items-center gap-3">
          {f.portfolio_count > 0 && (
            <span className="flex items-center gap-1">
              <Briefcase className="w-3 h-3" />{f.portfolio_count}
            </span>
          )}
          {f.daily_rate && (
            <span className="text-emerald-600 font-semibold">{f.daily_rate} {f.daily_rate_currency}/gün</span>
          )}
          <ChevronRight className="w-4 h-4 text-red-400 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

export default function PublicFindFreelancers() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [freelancers, setFreelancers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [availability, setAvailability] = useState(searchParams.get('availability') || '');
  const [country, setCountry] = useState(searchParams.get('country') || '');

  useEffect(() => {
    fetch(`${API}/public/freelancer-categories`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setCategories(d?.categories || []))
      .catch(() => {});
  }, []);

  const fetchFreelancers = (p = 1) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, limit: 24 });
    if (query) params.set('q', query);
    if (category) params.set('category', category);
    if (availability) params.set('availability', availability);
    if (country) params.set('country', country);

    fetch(`${API}/public/freelancers?${params}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        setFreelancers(d?.items || []);
        setTotal(d?.total || 0);
        setPage(p);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchFreelancers(1); }, [query, category, availability, country]);

  const totalPages = Math.ceil(total / 24);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-950 via-red-950 to-gray-900 py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-gray-400 hover:text-white text-sm mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />Ana Sayfa
          </button>
          <div className="text-center">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-red-400 mb-3">Freelancer Dizini</span>
            <h1 className="text-4xl sm:text-5xl font-black text-white mb-3">
              Stand Sektörü<br className="hidden sm:block" /> Freelancer'ları
            </h1>
            <p className="text-gray-400 text-base max-w-xl mx-auto">
              3D Tasarımcıdan Elektrikçiye, Koordinatörden Sahne Teknisyenine kadar 27+ kategoride uzman bulun.
            </p>
          </div>

          {/* Search bar */}
          <div className="mt-8 max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input value={query} onChange={e => setQuery(e.target.value)}
                placeholder="İsim, yetenek veya başlık ara..."
                className="w-full bg-white/10 backdrop-blur border border-white/20 text-white placeholder-gray-400 rounded-xl pl-12 pr-4 py-3 text-sm focus:outline-none focus:border-red-400 focus:bg-white/15 transition-all" />
            </div>
          </div>

          {/* KPI */}
          <div className="grid grid-cols-3 gap-4 mt-8 max-w-md mx-auto">
            {[
              { value: total, label: 'Freelancer' },
              { value: categories.length, label: 'Kategori' },
              { value: '27+', label: 'Uzmanlık' },
            ].map(({ value, label }) => (
              <div key={label} className="text-center">
                <p className="text-2xl font-black text-red-400">{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filters + Results */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Filter bar */}
        <div className="flex flex-wrap gap-3 mb-6">
          {/* Category filter */}
          <select value={category} onChange={e => setCategory(e.target.value)}
            className="text-sm border border-gray-200 rounded-xl px-4 py-2 bg-white focus:outline-none focus:border-red-400 min-w-[160px]">
            <option value="">Tüm Kategoriler</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          {/* Availability */}
          <select value={availability} onChange={e => setAvailability(e.target.value)}
            className="text-sm border border-gray-200 rounded-xl px-4 py-2 bg-white focus:outline-none focus:border-red-400">
            <option value="">Tüm Durumlar</option>
            <option value="available">Müsait</option>
            <option value="busy">Meşgul</option>
          </select>

          {/* Country */}
          <input value={country} onChange={e => setCountry(e.target.value)}
            placeholder="Ülke filtrele..."
            className="text-sm border border-gray-200 rounded-xl px-4 py-2 bg-white focus:outline-none focus:border-red-400 w-40" />

          {(category || availability || country || query) && (
            <button onClick={() => { setCategory(''); setAvailability(''); setCountry(''); setQuery(''); }}
              className="text-sm text-red-600 hover:text-red-800 px-3 py-2 rounded-xl border border-red-200 hover:border-red-400 transition-colors">
              Filtreleri Temizle
            </button>
          )}

          <div className="ml-auto text-sm text-gray-500 flex items-center">
            <span className="font-semibold text-gray-900">{total}</span>&nbsp;freelancer bulundu
          </div>
        </div>

        {/* Category quick chips */}
        {!category && (
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.slice(0, 10).map(c => (
              <button key={c.id} onClick={() => setCategory(c.id)}
                className="text-xs px-3 py-1.5 rounded-full border border-gray-200 text-gray-600 bg-white hover:border-red-400 hover:text-red-600 hover:bg-red-50 transition-all">
                {c.name}
              </button>
            ))}
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : freelancers.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-red-300" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Sonuç bulunamadı</h3>
            <p className="text-gray-500 text-sm">Filtrelerinizi değiştirmeyi deneyin</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {freelancers.map(f => <FreelancerCard key={f.org_id} f={f} />)}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button onClick={() => fetchFreelancers(page - 1)} disabled={page === 1}
              className="px-4 py-2 text-sm border border-gray-200 rounded-xl disabled:opacity-40 hover:border-red-400 transition-colors">
              Önceki
            </button>
            <span className="text-sm text-gray-500">{page} / {totalPages}</span>
            <button onClick={() => fetchFreelancers(page + 1)} disabled={page === totalPages}
              className="px-4 py-2 text-sm border border-gray-200 rounded-xl disabled:opacity-40 hover:border-red-400 transition-colors">
              Sonraki
            </button>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-red-600 to-rose-700 rounded-3xl p-8 text-center text-white">
          <h3 className="text-2xl font-black mb-2">Freelancer mısın?</h3>
          <p className="text-red-200 text-sm mb-5">Profilini oluştur, stand sektöründen iş almaya başla.</p>
          <Link to="/freelancer/signup">
            <Button className="bg-white text-red-700 hover:bg-red-50 font-bold px-8">
              Ücretsiz Kaydol →
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
